/**
 * 题目主标题富文本编辑器（0901 新增）。
 * - 受控的 contenteditable：内部用 ref 操作，外部通过 value / onChange 同步
 * - 工具条：加粗 / 斜体 / 下划线 / 颜色 / 字号 / 字体 / 对齐（左中右）/ 超链接 / 清除格式
 * - 兼容老数据：value 为 string 时自动包装为 RichText
 * - value 变更（外部 setConfig）时通过 valueVersion 同步到 DOM，不抢光标
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  type RichText,
  type RichTextFormat,
  ensureRichText,
} from './types';

type Props = {
  value: string | RichText | undefined | null;
  onChange: (next: RichText) => void;
  placeholder?: string;
  /** 编辑器所属题型：仅 statement / 普通题号时显示工具条，pagebreak 不允许编辑 */
  disabled?: boolean;
  /** className 注入到 contenteditable 节点 */
  inputClassName?: string;
  /** aria-label */
  ariaLabel?: string;
  searchField?: string;
};

const FONT_SIZES: Array<{ label: string; value: string }> = [
  { label: '12px', value: '12px' },
  { label: '14px', value: '14px' },
  { label: '16px', value: '16px' },
  { label: '18px', value: '18px' },
  { label: '20px', value: '20px' },
  { label: '24px', value: '24px' },
  { label: '28px', value: '28px' },
  { label: '32px', value: '32px' },
];

const FONT_FAMILIES: Array<{ label: string; value: string }> = [
  { label: '默认', value: '' },
  { label: '宋体', value: 'SimSun, "宋体", serif' },
  { label: '黑体', value: 'SimHei, "黑体", sans-serif' },
  { label: '微软雅黑', value: '"Microsoft YaHei", "微软雅黑", sans-serif' },
  { label: '苹方', value: '"PingFang SC", "苹方", sans-serif' },
  { label: '楷体', value: 'KaiTi, "楷体", serif' },
  { label: '等线', value: 'DengXian, "等线", sans-serif' },
];

const COLORS: string[] = [
  '#000000', '#5b5b5b', '#9e9e9e', '#cfcfcf', '#e5e5e5', '#f5f5f5', '#ffffff',
  '#fff5b1', '#ffd54f', '#ffb300', '#ff8f00', '#ff6f00', '#e65100', '#bf360c',
  '#ffe6e6', '#ff8a80', '#ff5252', '#ff1744', '#d50000', '#b71c1c', '#880e4f',
  '#ffd1dc', '#ff80ab', '#ff4081', '#f50057', '#c51162', '#ad1457', '#880e4f',
  '#f8bbd0', '#f48fb1', '#ec407a', '#e91e63', '#9c27b0', '#7b1fa2', '#4a148c',
  '#e1bee7', '#ce93d8', '#ab47bc', '#9c27b0', '#673ab7', '#5e35b1', '#311b92',
  '#d1c4e9', '#b39ddb', '#9575cd', '#7e57c2', '#5e35b1', '#3f51b5', '#1a237e',
  '#c5cae9', '#9fa8da', '#7986cb', '#3f51b5', '#303f9f', '#283593', '#0d47a1',
  '#bbdefb', '#90caf9', '#64b5f6', '#2196f3', '#1976d2', '#1565c0', '#0d47a1',
  '#b3e5fc', '#81d4fa', '#4fc3f7', '#03a9f4', '#0288d1', '#0277bd', '#01579b',
  '#b2ebf2', '#80deea', '#4dd0e1', '#00bcd4', '#0097a7', '#006064', '#004d40',
  '#c8e6c9', '#a5d6a7', '#81c784', '#4caf50', '#388e3c', '#2e7d32', '#1b5e20',
  '#dcedc8', '#c5e1a5', '#aed581', '#8bc34a', '#689f38', '#558b2f', '#33691e',
  '#f0f4c3', '#e6ee9c', '#dce775', '#cddc39', '#afb42b', '#9e9d24', '#827717',
  '#fff9c4', '#fff59d', '#fff176', '#ffeb3b', '#fdd835', '#fbc02d', '#f57f17',
  '#ffccbc', '#ffab91', '#ff8a65', '#ff7043', '#f4511e', '#e64a19', '#bf360c',
];

export default function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled,
  inputClassName,
  ariaLabel,
  searchField,
}: Props) {
  const editableRef = useRef<HTMLDivElement>(null);
  const lastHtmlRef = useRef<string>('');
  const skipInputRef = useRef(false);
  const [focused, setFocused] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  const r = ensureRichText(value);

  // 外部 value 变更 → 同步到 DOM；只在 text/html 与当前不一致时写入，避免抢光标
  useEffect(() => {
    const el = editableRef.current;
    if (!el) return;
    const desired = r.html ?? toPlainHTML(r);
    if (el.innerHTML !== desired) {
      skipInputRef.current = true;
      el.innerHTML = desired;
      lastHtmlRef.current = desired;
    }
    // 只依赖 value 的"内容指纹"——text 和 html 的内容决定是否同步
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r.text, r.html]);

  const emitChange = useCallback(() => {
    const el = editableRef.current;
    if (!el) return;
    const html = el.innerHTML;
    const text = toTitleText(el);
    if (html === lastHtmlRef.current && text === r.text) return;
    lastHtmlRef.current = html;
    onChange({ text, html, format: r.format ?? {} });
  }, [onChange, r.text, r.format]);

  const handleInput = () => {
    if (skipInputRef.current) { skipInputRef.current = false; return; }
    emitChange();
  };

  const exec = (cmd: string, valueArg?: string) => {
    editableRef.current?.focus();
    document.execCommand(cmd, false, valueArg);
    emitChange();
  };

  const wrapBlock = (tag: 'h1' | 'h2' | 'h3') => {
    editableRef.current?.focus();
    document.execCommand('formatBlock', false, tag);
    emitChange();
  };

  const insertLink = () => {
    const url = window.prompt('请输入链接 URL', 'https://');
    if (!url) return;
    exec('createLink', url);
  };

  const clearFormat = () => {
    editableRef.current?.focus();
    document.execCommand('removeFormat');
    document.execCommand('unlink');
    emitChange();
  };

  const applyFontSize = (size: string) => exec('fontSize', size);
  // 注：浏览器原生 fontSize 仅支持 1-7；这里走自定义方案（包一层 span）
  const applyFontSizeCustom = (size: string) => {
    const el = editableRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) return;
    const span = document.createElement('span');
    span.style.fontSize = size;
    try {
      span.appendChild(range.extractContents());
      range.insertNode(span);
      sel.removeAllRanges();
      const next = document.createRange();
      next.selectNodeContents(span);
      sel.addRange(next);
    } catch {
      // ignore
    }
    emitChange();
  };

  const applyFontFamily = (family: string) => {
    if (!family) return exec('removeFormat');
    const el = editableRef.current;
    if (!el) return;
    el.focus();
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (range.collapsed) return;
    const span = document.createElement('span');
    span.style.fontFamily = family;
    try {
      span.appendChild(range.extractContents());
      range.insertNode(span);
      sel.removeAllRanges();
      const next = document.createRange();
      next.selectNodeContents(span);
      sel.addRange(next);
    } catch {
      // ignore
    }
    emitChange();
  };

  const applyColor = (color: string) => {
    setShowColorPicker(false);
    if (!color) return exec('removeFormat');
    exec('foreColor', color);
  };

  const setAlign = (align: 'left' | 'center' | 'right') => {
    editableRef.current?.focus();
    document.execCommand('justifyLeft');
    if (align === 'center') document.execCommand('justifyCenter');
    if (align === 'right') document.execCommand('justifyRight');
    emitChange();
  };

  return (
    <div className={`rich-text-wrap${focused ? ' is-focused' : ''}`}>
      {!disabled && focused && (
        <div className="rich-text-toolbar" role="toolbar" aria-label="富文本格式">
          <ToolbarButton title="加粗 (Ctrl+B)" onClick={() => exec('bold')}><strong>B</strong></ToolbarButton>
          <ToolbarButton title="斜体 (Ctrl+I)" onClick={() => exec('italic')}><em>I</em></ToolbarButton>
          <ToolbarButton title="下划线 (Ctrl+U)" onClick={() => exec('underline')}><u>U</u></ToolbarButton>
          <ToolbarDivider />
          <ToolbarSelect
            title="字号"
            value=""
            onChange={(v) => v && applyFontSizeCustom(v)}
            options={[{ value: '', label: '字号' }, ...FONT_SIZES]}
          />
          <ToolbarSelect
            title="字体"
            value=""
            onChange={(v) => applyFontFamily(v)}
            options={FONT_FAMILIES}
          />
          <div className="rich-text-color">
            <button
              type="button"
              title="文字颜色"
              className="rich-text-tool-btn"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setShowColorPicker((v) => !v)}
            >
              <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 2, background: '#222', border: '1px solid var(--border)' }} />
            </button>
            {showColorPicker && (
              <div className="rich-text-color-pop" role="dialog" aria-label="颜色选择">
                <button type="button" className="rich-text-color-clear" onMouseDown={(e) => e.preventDefault()} onClick={() => applyColor('')}>清除文字颜色</button>
                <div className="rich-text-color-grid">
                  {COLORS.map((c) => (
                    <button
                      type="button"
                      key={c}
                      title={c}
                      aria-label={`选择颜色 ${c}`}
                      className="rich-text-color-cell"
                      style={{ background: c }}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => applyColor(c)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
          <ToolbarDivider />
          <ToolbarButton title="左对齐" onClick={() => setAlign('left')}>⟸</ToolbarButton>
          <ToolbarButton title="居中对齐" onClick={() => setAlign('center')}>≡</ToolbarButton>
          <ToolbarButton title="右对齐" onClick={() => setAlign('right')}>⟹</ToolbarButton>
          <ToolbarDivider />
          <ToolbarButton title="H1 大标题" onClick={() => wrapBlock('h1')}>H1</ToolbarButton>
          <ToolbarButton title="H2 中标题" onClick={() => wrapBlock('h2')}>H2</ToolbarButton>
          <ToolbarButton title="正文段落" onClick={() => wrapBlock('p')}>P</ToolbarButton>
          <ToolbarDivider />
          <ToolbarButton title="插入超链接" onClick={insertLink}>🔗</ToolbarButton>
          <ToolbarButton title="清除格式" onClick={clearFormat}>⌫</ToolbarButton>
        </div>
      )}
      <div
        ref={editableRef}
        className={`rich-text-input${inputClassName ? ' ' + inputClassName : ''}${r.text ? '' : ' is-empty'}`}
        contentEditable={!disabled}
        suppressContentEditableWarning
        spellCheck={false}
        role="textbox"
        aria-multiline="true"
        aria-label={ariaLabel ?? '富文本题干'}
        data-find-field={searchField}
        data-placeholder={placeholder ?? '请输入题干'}
        onInput={handleInput}
        onBlur={() => { setFocused(false); emitChange(); }}
        onFocus={() => setFocused(true)}
        onPaste={(e) => {
          // 粘贴时只保留纯文本，避免拖入垃圾格式
          e.preventDefault();
          const text = e.clipboardData.getData('text/plain');
          document.execCommand('insertText', false, text);
        }}
        onKeyDown={(e) => {
          // Cmd/Ctrl+Shift+1/3/5 切换字号
          if ((e.metaKey || e.ctrlKey) && e.shiftKey && ['1', '3', '5'].includes(e.key)) {
            e.preventDefault();
            const sizes = ['12px', '20px', '28px'];
            applyFontSizeCustom(sizes[Number(e.key) === 1 ? 0 : Number(e.key) === 3 ? 1 : 2] ?? '16px');
          }
        }}
      />
    </div>
  );
}

function ToolbarButton({ title, onClick, children }: { title: string; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" className="rich-text-tool-btn" title={title} aria-label={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}>
      {children}
    </button>
  );
}

function ToolbarDivider() {
  return <span className="rich-text-tool-divider" aria-hidden="true" />;
}

function ToolbarSelect({
  title, value, onChange, options,
}: {
  title: string;
  value: string;
  onChange: (v: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value);
  return (
    <div className="rich-text-tool-select">
      <button
        type="button"
        className="rich-text-tool-btn rich-text-select-btn"
        title={title}
        aria-label={title}
        aria-haspopup="listbox"
        aria-expanded={open}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => setOpen((v) => !v)}
      >
        {current?.label ?? title}
        <span className="rich-text-select-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="rich-text-select-pop" role="listbox" aria-label={title}>
          {options.map((o) => (
            <button
              type="button"
              key={o.value || 'empty'}
              className={`rich-text-select-item${o.value === value ? ' is-active' : ''}`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** 把 RichText 转成 innerHTML：富文本对象保留 html/format；纯文本走 escapeHTML */
function toPlainHTML(r: RichText): string {
  if (r.html && r.html.trim() !== '') return r.html;
  const f: RichTextFormat = r.format ?? {};
  const text = escapeHTMLSimple(r.text ?? '');
  const openStyle: string[] = [];
  if (f.fontFamily) openStyle.push(`font-family:${f.fontFamily}`);
  if (f.fontSize) openStyle.push(`font-size:${f.fontSize}`);
  if (f.color) openStyle.push(`color:${f.color}`);
  const tagOpen = openStyle.length ? `<span style="${openStyle.join(';')}">` : '';
  const tagClose = openStyle.length ? `</span>` : '';
  let html = `${tagOpen}${text}${tagClose}`;
  if (f.bold) html = `<strong>${html}</strong>`;
  if (f.italic) html = `<em>${html}</em>`;
  if (f.underline) html = `<u>${html}</u>`;
  if (f.align) html = `<div style="text-align:${f.align}">${html}</div>`;
  return html;
}

function escapeHTMLSimple(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}

/** 从 contenteditable DOM 中提取纯文本（用于 onChange 的 text 字段） */
function toTitleText(el: HTMLElement): string {
  // innerText 会受 CSS white-space 影响，但比 textContent 更贴近"看到的"
  return el.innerText.replace(/\u00a0/g, ' ');
}
